import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  listAdminUsers,
  createAdminUser,
  updateAdminUser,
  deleteAdminUser,
  type CreateAdminUserDTO,
  type UpdateAdminUserDTO,
} from '../api/admin-users.api.ts';
import type { AdminUserListParams } from '@/shared/types/admin-user.ts';

export const adminUserKeys = {
  all: ['admin-users'] as const,
  list: (params: AdminUserListParams) => [...adminUserKeys.all, 'list', params] as const,
  detail: (id: string) => [...adminUserKeys.all, 'detail', id] as const,
};

export function useAdminUserList(params: AdminUserListParams, enabled = true) {
  return useQuery({
    queryKey: adminUserKeys.list(params),
    queryFn: () => listAdminUsers(params),
    enabled,
  });
}

export function useCreateAdminUser(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAdminUserDTO) => createAdminUser(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
      toast.success('Usuário criado com sucesso');
      onSuccess?.();
    },
    onError: (err: unknown) => {
      if (isAxiosError(err) && err.response?.status === 409) {
        toast.error('Email já cadastrado');
      } else {
        toast.error('Erro ao criar usuário');
      }
    },
  });
}

export function useUpdateAdminUser(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateAdminUserDTO }) =>
      updateAdminUser(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
      toast.success('Usuário atualizado com sucesso');
      onSuccess?.();
    },
    onError: (err: unknown) => {
      if (isAxiosError(err) && err.response?.status === 422) {
        toast.error(err.response?.data?.error ?? 'Não é possível realizar esta ação');
      } else {
        toast.error('Erro ao atualizar usuário');
      }
    },
  });
}

export function useToggleNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, current }: { id: string; current: boolean }) =>
      updateAdminUser(id, { recebeNotificacoes: !current }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
      toast.success(
        variables.current
          ? 'Notificações desativadas'
          : 'Notificações ativadas',
      );
    },
    onError: () => {
      toast.error('Erro ao alterar notificações');
    },
  });
}

export function useDeleteAdminUser(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteAdminUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
      toast.success('Usuário excluído com sucesso');
      onSuccess?.();
    },
    onError: (err: unknown) => {
      if (isAxiosError(err) && err.response?.status === 422) {
        toast.error(err.response?.data?.error ?? 'Não é possível excluir este usuário');
      } else {
        toast.error('Erro ao excluir usuário');
      }
    },
  });
}

function isAxiosError(
  err: unknown,
): err is { response?: { status: number; data?: { error?: string } } } {
  return typeof err === 'object' && err !== null && 'response' in err;
}
